import React from "react";
import { motion } from "framer-motion";
import { POS, NEG } from "../lib/format";

export const Card = ({ children, className = "", glow = false, testid, ...rest }) => (
  <div data-testid={testid} className={`ao-card ${glow ? "ao-card-glow" : ""} ${className}`} {...rest}>
    {children}
  </div>
);

export const Overline = ({ children, className = "" }) => (
  <div className={`overline ${className}`}>{children}</div>
);

export const Delta = ({ value, suffix = "%", className = "", digits = 2 }) => {
  const up = value >= 0;
  return (
    <span className={`font-mono tabular ${className}`} style={{ color: up ? POS : NEG }}>
      {up ? "▲" : "▼"} {Math.abs(Number(value)).toFixed(digits)}{suffix}
    </span>
  );
};

export const Badge = ({ children, color = "#00e5ff", className = "" }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded font-mono text-[11px] font-medium ${className}`}
    style={{ color, background: `${color}1f`, border: `1px solid ${color}55` }}
  >
    {children}
  </span>
);

export const LiveBadge = ({ live, simulated }) => (
  <span className="inline-flex items-center gap-1.5 font-mono text-[10px]"
    style={{ color: live ? POS : "#64748b" }}>
    <span className={`w-1.5 h-1.5 rounded-full ${live ? "live-dot" : ""}`}
      style={{ background: live ? POS : "#64748b" }} />
    {live ? (simulated ? "SIM" : "LIVE") : "EOD"}
  </span>
);

export const MetricCard = ({ label, value, sub, accent = "#f8fafc", testid, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay }}
    className="ao-card p-4 hover:-translate-y-0.5" data-testid={testid}
  >
    <Overline>{label}</Overline>
    <div className="font-mono tabular text-xl lg:text-2xl font-semibold mt-1.5" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-xs text-[#64748b] mt-1">{sub}</div>}
  </motion.div>
);

export const ScoreBar = ({ value, color = "#00e5ff" }) => (
  <div className="h-1.5 w-full rounded-full bg-[#1e293b] overflow-hidden">
    <div className="h-full rounded-full" style={{ width: `${Math.min(100, value)}%`, background: color }} />
  </div>
);

export const Loader = ({ label = "Loading market data…" }) => (
  <div className="flex items-center justify-center py-24">
    <div className="flex flex-col items-center gap-3">
      <div className="w-6 h-6 rounded-full border-2 border-[#1e293b] border-t-[#00e5ff] animate-spin" />
      <span className="font-mono text-xs text-[#64748b]">{label}</span>
    </div>
  </div>
);

export const Spark = ({ data = [], color = "#00e5ff", w = 120, h = 32 }) => {
  if (!data || data.length < 2) return <div style={{ width: w, height: h }} />;
  const min = Math.min(...data), max = Math.max(...data);
  const rng = max - min || 1;
  const pts = data.map((d, i) => `${(i / (data.length - 1)) * w},${h - ((d - min) / rng) * h}`).join(" ");
  const up = data[data.length - 1] >= data[0];
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={up ? POS : NEG} strokeWidth="1.5" />
    </svg>
  );
};
